import { useState } from "react";
import { CaretRight, Scales, ShieldCheck, Buildings } from "@phosphor-icons/react";

const TYPE_META = {
  legal: { label: "Legally / operationally required", cls: "text-slate-700 bg-slate-100 border-slate-200", Icon: ShieldCheck },
  policy: { label: "Company policy (optional)", cls: "text-amber-800 bg-amber-50 border-amber-200", Icon: Buildings },
  unknown: { label: "Reason not stated", cls: "text-slate-500 bg-slate-50 border-slate-200", Icon: Scales },
};

export function TalentReachPanel({ data }) {
  const [open, setOpen] = useState(null);
  if (!data) return <p className="mt-6 text-sm text-slate-400" data-testid="talent-reach-loading">Analysing talent reach…</p>;
  const rows = data.excluded_rows || [];
  return (
    <div data-testid="talent-reach-panel">
      <p className="text-sm text-slate-600">Transparency analytics — how your location and work-model requirements affect the talent you can actually reach. This never recommends ignoring a legal requirement.</p>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <Stat label="Otherwise-matching pool" value={data.universe} />
        <Stat label="Remain eligible" value={data.eligible + data.possibly_eligible} cls="text-emerald-700" />
        <Stat label="Excluded" value={data.excluded} cls="text-red-700" />
      </div>

      <div className="mt-5 space-y-2">
        <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Excluded by requirement</p>
        {rows.length === 0 && <p className="text-sm text-slate-500">No candidates are excluded by your current requirements.</p>}
        {rows.map((r, i) => {
          const meta = TYPE_META[r.restriction_type] || TYPE_META.unknown;
          return (
            <div key={r.reason_key} className="rounded-lg border border-slate-200 bg-white">
              <button type="button" onClick={() => setOpen(open === i ? null : i)} data-testid={`reach-row-${r.reason_key}`}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50">
                <span className="flex items-center gap-2 text-sm text-slate-800">
                  <CaretRight weight="bold" size={12} className={`text-slate-400 transition-transform ${open === i ? "rotate-90" : ""}`} />
                  {r.label}
                </span>
                <span className="flex items-center gap-2">
                  <span className={`text-[11px] rounded-full border px-2 py-0.5 flex items-center gap-1 ${meta.cls}`}><meta.Icon weight="fill" size={11} /> {meta.label}</span>
                  <span className="font-mono-num text-red-700 font-semibold">{r.count}</span>
                </span>
              </button>
              {open === i && (
                <ul className="px-4 pb-3 space-y-1 border-t border-slate-100 pt-2" data-testid={`reach-drill-${r.reason_key}`}>
                  {r.examples.map((ex, ei) => (
                    <li key={ei} className="text-xs text-slate-600"><span className="font-medium text-slate-800">{ex.name}:</span> {ex.explanation}</li>
                  ))}
                  {r.count > r.examples.length && <li className="text-xs text-slate-400">+{r.count - r.examples.length} more</li>}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, cls = "text-slate-900" }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 font-mono-num font-semibold text-2xl ${cls}`}>{value ?? "—"}</p>
    </div>
  );
}
